// Nomad-mon Go! — SC2-B P5: SHAO BATTLE COMMENTARY. 36 pre-staged zinger lines
// (assets/vo-battle/*, manifest-described) fired off battle events through the
// audio vo channel, so ducking + no-overlap come free. A global ~8s cooldown
// keeps Shao a color man, not a babbler; gastro/ko/ult/vic/def always cut
// through (interrupting, ignoring — and resetting — the cooldown clock).
// battle.js emits through the single on(event, payload) call per event site.

import * as audio from './audio.js';

const V = 'v188';
const COOLDOWN = 8;                                 // seconds between lines
const PRIORITY = new Set(['gastro', 'ko', 'ult', 'vic', 'def',
  'firstblood', 'comeback']);   // SC14: match-defining beats always cut through
const EXPL_KEY = 'nomadmon-shao-explicit';

let manifest = null, loadP = null;
const bags = new Map();                             // pool -> shuffle bag
let lastAt = -1e9;                                  // perf-clock of last line
let battleOnce = new Set();

// Presentation-only battle callouts that have no VO asset. The battle owns
// their DOM placement; this module owns the commentary wording and the strict
// once-per-battle/per-side rule.
export function resetBattleOnce() { battleOnce = new Set(); }
export function oneMore(side, show) {
  const key = `one-more:${side}`;
  if (battleOnce.has(key)) return false;
  battleOnce.add(key);
  show?.('ONE MORE');
  console.info(`[shao] one-more line "ONE MORE" (${side})`);
  return true;
}

// EXPLICIT SHAO toggle — default ON; OFF filters to tag === 'clean' only
export function explicitOn() {
  try { return localStorage.getItem(EXPL_KEY) !== '0'; } catch { return true; }
}
export function setExplicit(on) {
  try { localStorage.setItem(EXPL_KEY, on ? '1' : '0'); } catch { /* private */ }
}

export function preload() {
  if (loadP) return loadP;
  loadP = fetch(`./assets/vo-battle/manifest.json?${V}`)
    .then(r => r.json()).then(m => { manifest = m; })
    .catch((e) => { console.warn('[shao] manifest missing', e); manifest = []; });
  return loadP;
}

function shuffle(a) {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// random non-repeating within pool: per-pool shuffle bags, refilled on empty
function pickLine(pool) {
  if (!manifest) return null;
  const legal = manifest.filter(l => l.pool === pool && (explicitOn() || l.tag === 'clean'));
  if (!legal.length) return null;
  let bag = bags.get(pool);
  if (!bag || !bag.length) { bag = shuffle(legal.slice()); bags.set(pool, bag); }
  // skip lines the explicit toggle has since outlawed
  while (bag.length) {
    const cand = bag.pop();
    if (explicitOn() || cand.tag === 'clean') return cand;
  }
  return legal[Math.floor(Math.random() * legal.length)];
}

// on(event, payload) -> true if a line actually fired (callers may then skip
// their legacy generic narrator line at the same site)
export function on(event, payload = {}) {
  if (!audio.narratorOn()) return false;            // narrator OFF = no Shao
  let pool = event;
  if (event === 'hit') {
    if ((payload.dmg || 0) >= 30 || payload.effective) pool = 'hit';
    else if ((payload.dmg || 0) <= 12) pool = 'weak';
    else return false;                              // mid damage — no comment
  }
  if (event === 'round' && Math.random() > 0.25) return false;   // ~25% only
  const now = performance.now() / 1000;
  const since = now - lastAt;
  const pri = PRIORITY.has(pool);
  if (!pri && since < COOLDOWN) {
    console.info('[shao] suppressed (cooldown)');
    return false;
  }
  const line = pickLine(pool);
  if (!line) return false;
  lastAt = now;                                     // priority resets the clock
  console.info(`[shao] ${pool} line "${line.text}" (cooldown ${since > 999 ? '—' : since.toFixed(1)}s)`);
  audio.voFile(line.file, {
    gain: 1.05,
    interrupt: pri,
    holdDuck: pool === 'gastro',       // the gastro ceremony owns the silence
  });
  return true;
}
